import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Tabs, Table, Button, Tag, Space, Modal, Form, Input, Select, Switch,
  message, Popconfirm, Typography, Row, Col, Checkbox, Tooltip, Divider,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, LockOutlined, SafetyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchUsers, createUser, updateUser, deleteUser,
  fetchRoles, createRole, updateRole, deleteRole,
} from '../services/authService';
import { PERMISSIONS } from '../../auth/permissions';

const { Title, Text } = Typography;

const UsersAndRoles = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [roleModal, setRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [uForm] = Form.useForm();
  const [rForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try { const [u, r] = await Promise.all([fetchUsers(), fetchRoles()]); setUsers(u); setRoles(r); }
    catch { message.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ---- Users ----
  const openUser = (rec) => {
    setEditingUser(rec || null);
    uForm.resetFields();
    if (rec) uForm.setFieldsValue({ ...rec, IsActive: !!rec.IsActive, Password: undefined });
    else uForm.setFieldsValue({ IsActive: true });
    setUserModal(true);
  };
  const saveUser = async () => {
    let v; try { v = await uForm.validateFields(); } catch { return; }
    try {
      if (editingUser) await updateUser(editingUser.UserID, v);
      else await createUser(v);
      message.success('Saved'); setUserModal(false); load();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to save'); }
  };
  const removeUser = async (rec) => {
    try { await deleteUser(rec.UserID); message.success('Deleted'); load(); }
    catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  // ---- Roles ----
  const openRole = (rec) => {
    setEditingRole(rec || null);
    rForm.resetFields();
    if (rec) rForm.setFieldsValue({ Name: rec.Name, Description: rec.Description, Permissions: rec.Permissions || [] });
    else rForm.setFieldsValue({ Permissions: ['view_dashboard'] });
    setRoleModal(true);
  };
  const saveRole = async () => {
    let v; try { v = await rForm.validateFields(); } catch { return; }
    try {
      if (editingRole) await updateRole(editingRole.RoleID, v);
      else await createRole(v);
      message.success('Saved'); setRoleModal(false); load();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to save'); }
  };
  const removeRole = async (rec) => {
    try { await deleteRole(rec.RoleID); message.success('Deleted'); load(); }
    catch (e) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const userColumns = [
    { title: 'Name', dataIndex: 'Name', render: (t, r) => <Space><UserOutlined /><div><Text strong>{t}</Text><div><Text type="secondary" style={{ fontSize: 12 }}>@{r.Username}</Text></div></div></Space> },
    { title: 'Role', dataIndex: 'RoleName', render: (t) => t ? <Tag color="blue">{t}</Tag> : '—' },
    { title: 'Email', dataIndex: 'Email', render: (t) => t || '—' },
    { title: 'Mobile', dataIndex: 'Mobile', render: (t) => t || '—' },
    { title: 'Active', dataIndex: 'IsActive', width: 80, align: 'center', render: (v) => v ? <Tag color="green">Active</Tag> : <Tag>Disabled</Tag> },
    { title: 'Last Login', dataIndex: 'LastLoginAt', width: 160, render: (d) => d ? dayjs(d).format('DD MMM, hh:mm A') : '—' },
    { title: 'Actions', key: 'a', width: 110, align: 'center', render: (_, r) => (
      <Space size={0}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openUser(r)} />
        <Popconfirm title="Delete this user?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeUser(r)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={r.Username === 'vastuvit'} />
        </Popconfirm>
      </Space>
    ) },
  ];

  const roleColumns = [
    { title: 'Role', dataIndex: 'Name', render: (t, r) => <Space><SafetyOutlined />{t}{r.IsSystem ? <Tooltip title="System role"><LockOutlined style={{ color: '#bfbfbf' }} /></Tooltip> : null}</Space> },
    { title: 'Description', dataIndex: 'Description', render: (t) => t || '—' },
    { title: 'Permissions', dataIndex: 'Permissions', render: (p) => <Tag>{(p || []).length} permissions</Tag> },
    { title: 'Users', dataIndex: 'UserCount', width: 80, align: 'center' },
    { title: 'Actions', key: 'a', width: 110, align: 'center', render: (_, r) => (
      <Space size={0}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openRole(r)} />
        <Popconfirm title="Delete this role?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeRole(r)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={!!r.IsSystem} />
        </Popconfirm>
      </Space>
    ) },
  ];

  // group permissions for the checkbox editor
  const groups = PERMISSIONS.reduce((a, p) => { (a[p.group] = a[p.group] || []).push(p); return a; }, {});

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Users &amp; Roles</Title>
          <Text type="secondary">Create users and control access by role</Text>
        </Col>
      </Row>

      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Tabs
          defaultActiveKey="users"
          items={[
            {
              key: 'users', label: 'Users',
              children: (
                <>
                  <div style={{ textAlign: 'right', marginBottom: 12 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openUser(null)}>Add User</Button>
                  </div>
                  <Table size="small" bordered rowKey="UserID" loading={loading} dataSource={users} columns={userColumns} pagination={false} />
                </>
              ),
            },
            {
              key: 'roles', label: 'Roles & Permissions',
              children: (
                <>
                  <div style={{ textAlign: 'right', marginBottom: 12 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openRole(null)}>Add Role</Button>
                  </div>
                  <Table size="small" bordered rowKey="RoleID" loading={loading} dataSource={roles} columns={roleColumns} pagination={false} />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* User modal */}
      <Modal title={editingUser ? 'Edit User' : 'Add User'} open={userModal} onCancel={() => setUserModal(false)} onOk={saveUser} okText="Save" destroyOnClose>
        <Form form={uForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="Name" label="Full Name" rules={[{ required: true, message: 'Enter name' }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="Username" label="Username" rules={[{ required: true, message: 'Enter username' }]}><Input disabled={!!editingUser} /></Form.Item></Col>
          </Row>
          <Form.Item name="Password" label={editingUser ? 'New Password (leave blank to keep)' : 'Password'}
            rules={editingUser ? [] : [{ required: true, message: 'Enter password' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
          </Form.Item>
          <Form.Item name="RoleID" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
            <Select options={roles.map(r => ({ value: r.RoleID, label: r.Name }))} placeholder="Select role" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="Email" label="Email"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="Mobile" label="Mobile"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="IsActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      {/* Role modal */}
      <Modal title={editingRole ? 'Edit Role' : 'Add Role'} open={roleModal} onCancel={() => setRoleModal(false)} onOk={saveRole} okText="Save" width={640} destroyOnClose>
        <Form form={rForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="Name" label="Role Name" rules={[{ required: true, message: 'Enter role name' }]}><Input disabled={editingRole?.IsSystem} /></Form.Item></Col>
            <Col span={12}><Form.Item name="Description" label="Description"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="Permissions" label="Permissions">
            <Checkbox.Group style={{ width: '100%' }}>
              {Object.entries(groups).map(([g, perms]) => (
                <div key={g} style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 12, color: '#8c8c8c' }}>{g}</Text>
                  <Divider style={{ margin: '4px 0' }} />
                  <Row>
                    {perms.map(p => <Col span={12} key={p.key}><Checkbox value={p.key}>{p.label}</Checkbox></Col>)}
                  </Row>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersAndRoles;
